import OwTitle from "@components/shared/OwTitle/OwTitle";
import React, { useContext, useEffect, useState } from "react";
import ICatalog from "@models/ICatalog";
import SupplierContext from "@contexts/SupplierContext";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import Typeahead from "@components/shared/Typeahead/Typeahead";
import WhatsThis from "@components/shared/WhatsThis/WhatsThis";
import UserContext from "@contexts/UserContext";
import ITypeaheadCatalogItemDTO from "@models/DTO/ITypeaheadCatalogItemDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import { SocketContext } from "@contexts/SocketContext";
import IOnTypeaheadCatalogItemDTO from "@models/DTO/IOnTypeaheadCatalogItemDTO";
import ICatalog_Item from "@models/ICatalog_Item";
import IUiSocketSetup from "@models/IUiSocketSetup";
import TitleTagEnum from "@models/TitleTagEnum";
import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";

type Props = {
    handleChange: (e: React.ChangeEvent<any>) => void;
    borderClass: string;
    catalogs: ICatalog[];
};

export const QuickbooksDesktopConfigOrdersSection = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const [supplierIntegration, setSupplierIntegration] = useState({
        qbd_shipping_catalog_id: supplierModel.Supplier_Integration.qbd_shipping_catalog_id,
        qbd_show_ship_via_options: supplierModel.Supplier_Integration.qbd_show_ship_via_options || false,
    });

    const [shippingCatalogItems, setShippingCatalogItems] = useState<ICatalog_Item[]>([]);
    const [selectedShippingCatalogItem, setSelectedShippingCatalogItem] = useState<ICatalog_Item | null>(null);
    const [clearTypeahead, setClearTypeahead] = useState(false);

    const handleChange = (e: React.ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const updatedSupplierIntegration = {
            ...supplierIntegration,
            [name]: value,
        };

        setSupplierIntegration(updatedSupplierIntegration);

        props.handleChange(e);
    };

    const getShippingCatalogItem = (name: string) => {
        if (supplierIntegration.qbd_shipping_catalog_id && props.catalogs) {
            const foundCatalog = props.catalogs.find((catalog) => {
                return catalog.id === Number(supplierIntegration.qbd_shipping_catalog_id);
            });

            if (foundCatalog) {
                const catalogGuid = foundCatalog.guid;

                const payload: ITypeaheadCatalogItemDTO = {
                    userGuid: userModel.guid,
                    userToken: userModel.token,
                    supplierGuid: supplierModel.guid,
                    name,
                    catalogGuid,
                    options: {
                        searchName: true,
                        searchDescription: true,
                        searchSku: true,
                        searchUpc: true,
                    },
                };

                socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
            }
        }
    };

    const onTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        setShippingCatalogItems(data.catalog_items);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeaheadCatalogItem }];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    const selectShippingCatalogItem = (item: any) => {
        setSelectedShippingCatalogItem(item);

        props.handleChange({
            target: {
                name: "qbd_shipping_catalog_item_id",
                value: item.id,
            },
        } as React.ChangeEvent<any>);
    };

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
        setSelectedShippingCatalogItem(null);
    };

    return (
        <div className={"QuickbooksDesktopConfigOrdersSection mb-2"}>
            <OwAccordion title={"orders"} fullWidthClick={true} titleTag={TitleTagEnum.H5}>
                <div className={"col-12 card " + props.borderClass} data-id={"qbd-orders-card"}>
                    <div className={"card-header"}>
                        <OwTitle title={"orders"} isBold={true} tag={"h5"} />
                    </div>

                    <div className="card-body">
                        <div className={""}>
                            <OwLabel title={"shipping_item"} isBold={true} />
                            <WhatsThis subject={"qbd_shipping_item"} />
                            <div>
                                <OwSelectOption
                                    name={"qbd_shipping_catalog_id"}
                                    options={props.catalogs}
                                    fn={handleChange}
                                    titleProp={"name"}
                                    valueProp={"id"}
                                    selectedOption={supplierIntegration}
                                    selectedOptionProp={"qbd_shipping_catalog_id"}
                                />
                            </div>
                            <div className={"mt-2"}>
                                <Typeahead
                                    isSelectableFn={() => {
                                        return true;
                                    }}
                                    typeaheadApiFn={getShippingCatalogItem}
                                    typeaheadItems={shippingCatalogItems}
                                    titleProp={"name"}
                                    selectedItemFn={selectShippingCatalogItem}
                                    selectedItem={supplierModel.Supplier_Integration.QBD_Shipping_Catalog_Item}
                                    selectedItemTitleProp={"name"}
                                    clearTypeahead={clearTypeahead}
                                    clearTypeaheadCallback={clearTypeaheadFn}
                                />
                            </div>
                        </div>

                        <div className={"mt-4"}>
                            <OwToggle
                                fn={handleChange}
                                name={"qbd_show_ship_via_options"}
                                label={"show_ship_via_options"}
                                active={supplierIntegration.qbd_show_ship_via_options}
                                labelOnRight={true}
                            />
                        </div>
                    </div>
                </div>
            </OwAccordion>
        </div>
    );
};
